import { Dropdown } from "react-bootstrap";
import MyModal from "../../../Common/my-modal/my-modal.component";
import './invite-msp.modal.styles.scss';
import { useEffect, useMemo, useState } from "react";
import stringUtils from "../../../../Utils/string.utils";
import { HGHighlightText } from "../../../App/App";
import useDropdown from "../../../../hooks/dropdown.hook";
import useActions from "../../../../hooks/service-actions.hook";
import { MSPInviteEffectActions } from "../../../../common/services/msp-invite/msp-invite.service";
import { CountriesEffectActions } from "../../../../common/services/countries/countries.service";
import { useEntity } from "../../../../hooks/use-entity.hook";
import { Entities } from "../../../../common/store/entities/entities.enum";
import useLoaders from "../../../../hooks/use-loaders.hook";
import { Loaders } from "../../../../common/store/loaders/loaders.enum";
import { AlertLine } from "../../../Alert/alert-line";

const RowLabel = ({ label, children, required = false }) => {
  return (
    <div className="d-flex flex-column gap-4 row-label">
      <label className="f-12 line-height-18 f-500 f-grey m-0">
        {label}
        {required && <span>*</span>}
      </label>
      {children}
    </div>
  )
}

const InviteMspModal = ({ isOpen, onClose, onInviteSuccess, variant = 'v1' }) => {
  const [showAlert, setShowAlert] = useState(false);
  const { inviteMSP } = useActions(MSPInviteEffectActions);
  const { getCountries } = useActions(CountriesEffectActions);
  const { entityData: countries } = useEntity({ entityType: Entities.Countries, entityId: 'all' });
  const { isLoading: isInviteLoading } = useLoaders({ loaders: [Loaders.MspInviteLoader] });
  const [formData, setFormData] = useState({
    companyName: '',
    userName: '',
    companyEmail: '',
    country: '',
    plan: '',
  });
  const [errors, setErrors] = useState({
    companyName: '',
    userName: '',
    companyEmail: '',
    country: '',
    plan: '',
  });

  const isFormValid = useMemo(() => {
    if (variant !== 'v2') return true;
    
    const hasCompanyName = formData.companyName.trim() !== '';
    const hasUserName = formData.userName.trim() !== '';
    const hasValidEmail = formData.companyEmail.trim() !== '' && stringUtils.isEmail(formData.companyEmail);
    
    return hasCompanyName && hasUserName && hasValidEmail;
  }, [variant, formData.companyName, formData.userName, formData.companyEmail]);

  const validateForm = () => {
    const errors = {};
    if (!formData.companyName) errors.companyName = 'Company Name is required';
    if (!formData.userName) errors.userName = 'User Name is required';
    
    if (!formData.companyEmail) {
      errors.companyEmail = 'Company Email is required';
    } else if (!stringUtils.isEmail(formData.companyEmail)) {
      errors.companyEmail = 'Please enter a valid email address';
    }
    
    return errors;
  }

  const handleContinue = () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }
    inviteMSP({
      companyName: formData.companyName,
      userName: formData.userName,
      companyEmail: formData.companyEmail,
      country: formData.country?.objId,
      callback: () => {
        onInviteSuccess();
      }
    });
  }

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        companyName: '',
        userName: '',
        companyEmail: '',
        country: '',
        plan: '',
      });
      setErrors({
        companyName: '',
        userName: '',
        companyEmail: '',
        country: '',
        plan: '',
      });
    } else {
      getCountries();
    }
  }, [isOpen])
  return (
    <MyModal show={isOpen} onHide={onClose} className="invite-msp-modal">
      <MyModal.Body className="d-flex flex-column gap-16">
        <h2 className="title-row text-center">Invite MSP</h2>
        <p className="description-row text-center m-0 f-14 line-height-22 f-black">
          Enter user details below. We’ll email an activation link to the company address — this will grant them a free trial of HighGround.
        </p>

        {showAlert && <AlertLine message="This company already exists or has been offered a trial." />}
        <RowLabel label="Company Name" required>
          <input type="text" className={`greyout-input-common f-14 line-height-22 f-black w-100 ${errors.companyName ? 'error' : ''}`} value={formData.companyName} 
          onChange={(e) => {
            setFormData({ ...formData, companyName: e.target.value });
            setErrors({ ...errors, companyName: '' });
          }} 
        />
        </RowLabel>
        <RowLabel label={variant === 'v2' ? 'User Name' : 'Full Name'} required>
          <input type="text" className={`greyout-input-common f-14 line-height-22 f-black w-100 ${errors.userName ? 'error' : ''}`} value={formData.userName} 
          onChange={(e) => {
            setFormData({ ...formData, userName: e.target.value });
            setErrors({ ...errors, userName: '' });
          }} 
        />
        </RowLabel>
        <RowLabel label="Company Email" required>
          <input type="text" className={`greyout-input-common f-14 line-height-22 f-black w-100 ${errors.companyEmail ? 'error' : ''}`} value={formData.companyEmail} 
          onChange={(e) => {
            setFormData({ ...formData, companyEmail: e.target.value });
            setErrors({ ...errors, companyEmail: '' });
          }} 
        />
        </RowLabel>
        <RowLabel label="Country">
          <CountryDropdown 
            countries={countries}
            selectedCountry={formData?.country?.name} 
            onSelect={(country) => {
              setFormData({ ...formData, country });
              setErrors({ ...errors, country: '' });
            }} 
            hasError={errors.country} 
          />
        </RowLabel>
        {variant === 'v2' && (
          <RowLabel label="Most Likely Plan">
            <PlanDropdown 
              selectedPlan={formData?.plan?.name} 
              onSelect={(plan) => {
                setFormData({ ...formData, plan });
                setErrors({ ...errors, plan: '' });
              }} 
              hasError={errors.plan} 
            />
          </RowLabel>
        )}
      </MyModal.Body>
      <MyModal.Footer className="d-flex justify-content-end gap-8">
        <MyModal.CancelButton onClick={onClose}>
          <span>Cancel</span>
        </MyModal.CancelButton>
        <MyModal.ApplyButton 
          onClick={handleContinue} 
          loading={isInviteLoading} 
          disabled={isInviteLoading || (variant === 'v2' && !isFormValid)}
          className={variant === 'v2' && !isFormValid ? 'v2-disabled' : ''}
        >
          <span>Invite</span>
        </MyModal.ApplyButton>
      </MyModal.Footer>
    </MyModal>
  )
}

export default InviteMspModal;

const PLANS = [
  { objId: '1', name: 'Plan 1' },
  { objId: '2', name: 'Plan 2' },
];

const CountryDropdown = ({ selectedCountry, onSelect = () => {}, hasError = false, countries = [] }) => {
  const { isOpen, setIsOpen, refMenu, refBtn } = useDropdown({  })
  const [searchValue, setSearchValue] = useState('');

  const filteredCountries = useMemo(() => {
    return countries?.filter((country) => country?.name?.toLowerCase().includes(searchValue.toLowerCase()));
  }, [countries, searchValue]);

  return (
    <Dropdown className="transparennt-wrapper-dropdown my-dropdown my-dropdown-common w-100 invite-msp-modal-country-dropdown">
      <Dropdown.Toggle 
        ref={refBtn} className="w-100" onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        aria-expanded={isOpen}
      >
        <div className={`d-flex align-items-center gap-8 greyout-input-common f-14 line-height-22 f-black w-100 ${hasError ? 'error' : ''}`}>
          <input 
            type="text" 
            className="w-100 my-input line-height-22 f-14" 
            value={selectedCountry || searchValue} 
            onChange={(e) => {
              setSearchValue(e.target.value);
              onSelect(null);
              setIsOpen(true);
            }} 
          />
          <div className="arrow-icon" />
        </div>
      </Dropdown.Toggle>
      <Dropdown.Menu tabIndex={-1} show={isOpen} ref={refMenu} className="menu hide-scrollbar" popperConfig={{ strategy: 'fixed' }}>
        {filteredCountries?.length > 0 ? filteredCountries?.map((country) => (
          <div 
          key={country.objId} className="item" onClick={() => {
            onSelect(country);
            setSearchValue('');
            setIsOpen(false);
          }}>
            <span 
              dangerouslySetInnerHTML={{
                __html: HGHighlightText(country.name, searchValue)
              }}
            />
          </div>
        )) : (
          <div className="item disabled">
            <span>No countries found</span>
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  )
}

const PlanDropdown = ({ selectedPlan, onSelect = () => {}, hasError = false }) => {
  const { isOpen, setIsOpen, refMenu, refBtn } = useDropdown({  })
  const [searchValue, setSearchValue] = useState('');

  const filteredPlans = useMemo(() => {
    return PLANS?.filter((plan) => plan?.name?.toLowerCase().includes(searchValue.toLowerCase()));
  }, [searchValue]);

  return (
    <Dropdown className="transparennt-wrapper-dropdown my-dropdown my-dropdown-common w-100 invite-msp-modal-plan-dropdown">
      <Dropdown.Toggle 
        ref={refBtn} className="w-100" onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        aria-expanded={isOpen}
      >
        <div className={`d-flex align-items-center gap-8 greyout-input-common f-14 line-height-22 f-black w-100 ${hasError ? 'error' : ''}`}>
          <input 
            type="text" 
            className="w-100 my-input line-height-22 f-14" 
            value={selectedPlan || searchValue} 
            onChange={(e) => {
              setSearchValue(e.target.value);
              onSelect(null);
              setIsOpen(true);
            }} 
          />
          <div className="arrow-icon" />
        </div>
      </Dropdown.Toggle>
      <Dropdown.Menu tabIndex={-1} show={isOpen} ref={refMenu} className="menu hide-scrollbar" popperConfig={{ strategy: 'fixed' }}>
        {filteredPlans?.length > 0 ? filteredPlans?.map((plan) => (
          <div 
          key={plan.objId} className="item" onClick={() => {
            onSelect(plan);
            setSearchValue('');
            setIsOpen(false);
          }}>
            <span 
              dangerouslySetInnerHTML={{
                __html: HGHighlightText(plan.name, searchValue)
              }}
            />
          </div>
        )) : (
          <div className="item disabled">
            <span>No plans found</span>
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  )
}


